import { useEffect, useState, useMemo } from "react";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper } from "../../helpers/permission";
import { Loader } from "lucide-react";

const EmailConfigManagement = () => {
  const [emailConfig, setEmailConfig] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("authToken");

  // 🔎 Search Filters
  const [hostFilter, setHostFilter] = useState("");
  const [usernameFilter, setUsernameFilter] = useState("");
  const [emailFromFilter, setEmailFromFilter] = useState("");
  const [mailerNameFilter, setMailerNameFilter] = useState("");
  const [filterLoading, setFilterLoading] = useState(false);

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  const fetchEmailConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}settings/emailconfig/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setEmailConfig(response.data.data || []);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailConfig();
  }, []);

  // 🔎 Apply filters
  const filteredConfigs = useMemo(() => {
    return emailConfig.filter((config) => {
      if (
        hostFilter &&
        !(config.smtp_host || "")
          .toLowerCase()
          .includes(hostFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        usernameFilter &&
        !(config.smtp_username || "")
          .toLowerCase()
          .includes(usernameFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        emailFromFilter &&
        !(config.emailfrom || "")
          .toLowerCase()
          .includes(emailFromFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        mailerNameFilter &&
        !(config.mailer_name || "")
          .toLowerCase()
          .includes(mailerNameFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [emailConfig, hostFilter, usernameFilter, emailFromFilter, mailerNameFilter]);

  useEffect(() => {
    if (!emailConfig.length) return;

    setFilterLoading(true);
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [
    hostFilter,
    usernameFilter,
    emailFromFilter,
    mailerNameFilter,
    emailConfig,
  ]);

  // 📑 Update pagination when filters change
  useEffect(() => {
    setTotal(filteredConfigs.length);
    setPageCount(Math.ceil(filteredConfigs.length / limit));
    setPage(1);
  }, [filteredConfigs, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedConfigs = filteredConfigs.slice(start - 1, end);

  const [modalOpen, setModalOpen] = useState(false);
  const [mailData, setMailData] = useState({
    mailid: "",
    mailfrom: "",
    mail_to: "",
    mailcontent: "",
  });

  const checkMail = (mailid, mailfrom) => {
    setMailData({ ...mailData, mailid, mailfrom });
    setModalOpen(true);
  };

  // 🔴 UPDATED handleMailSubmit with API integration
  const handleMailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!mailData.mail_to || !mailData.mailcontent) {
      alert("Mail To and Content are required.");
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}settings/template/email/check-email`,
        {
          mailid: mailData.mailid,
          mailfrom: mailData.mailfrom,
          mail_to: mailData.mail_to,
          mailcontent: mailData.mailcontent,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        Swal.fire({
          title: "Mail Sent",
          text: "Success",
          icon: "success",
          confirmButtonClass: "btn-success",
        }).then(() => {
          setModalOpen(false);
          setMailData({
            mailid: "",
            mailfrom: "",
            mail_to: "",
            mailcontent: "",
          });
        });
      } else {
        Swal.fire("Error", response.data.message || "Mail sending failed.", "error");
      }
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Something went wrong while sending mail.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setHostFilter("");
      setUsernameFilter("");
      setEmailFromFilter("");
      setMailerNameFilter("");
    };
    window.addEventListener('clearEmailConfigFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearEmailConfigFilterEvent', handleClearFilters);
    };
  }, []);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_URL}settings/emailconfig/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        setEmailConfig((prev) => prev.filter((config) => config.id !== id));
        Swal.fire("Deleted!", response.data.message || "Your record has been deleted.", "success");
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Something went wrong while deleting.",
          "error"
        );
      }
    }
  };

  return (
    <div className="">
      <div className="row mt-5">
        <h4 className="col-md-6">Manage Email Configuration</h4>
        <div className="col-md-6 text-right">
          <PermissionWrapper module="settings_emailconfig" action="add"                     >
            <Link
              to="/Settings/EmailConfig/add"
              className="btn btn-default btn-sm"
            >
              <FontAwesomeIcon icon={faPlus} /> Add Email Configuration
            </Link>
          </PermissionWrapper>

        </div>
      </div>

      <div className="panel panel-default with-rounded-corner with-shadow mt-2">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 className="panel-title">Email Configurations</h4>
          </div>

          {/* 📑 Pagination Controls */}
          {emailConfig.length > 0 && (
            <div className="d-inline-block ms-3 me-3">
              <span style={{ fontSize: "0.8rem" }} className="me-2">
                {start} to {end} of {total}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pageCount}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>

        {loading || emailConfig.length ? (
          <div className="table-responsive">
            <table className="table m-b-0">
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>#</th>
                  <th>SMTP Host</th>
                  <th>SMTP Username</th>
                  <th>SMTP Secure</th>
                  <th>SMTP Port</th>
                  <th>Email From</th>
                  <th>Mailer Name</th>
                  <th>Action</th>
                </tr>
                {/* 🔎 Search Filters Row */}
                <tr>
                  <th></th>
                  <th>
                    <input
                      type="text"
                      className="form-control input-sm"
                      placeholder="Search host..."
                      value={hostFilter}
                      onChange={(e) => setHostFilter(e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      className="form-control input-sm"
                      placeholder="Search username..."
                      value={usernameFilter}
                      onChange={(e) => setUsernameFilter(e.target.value)}
                    />
                  </th>
                  <th></th>
                  <th></th>
                  <th>
                    <input
                      type="text"
                      className="form-control input-sm"
                      placeholder="Search email from..."
                      value={emailFromFilter}
                      onChange={(e) => setEmailFromFilter(e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      className="form-control input-sm"
                      placeholder="Search mailer name..."
                      value={mailerNameFilter}
                      onChange={(e) => setMailerNameFilter(e.target.value)}
                    />
                  </th>
                  <th>
                    <button
                      className="btn btn-default btn-sm"
                      onClick={() => {
                        setHostFilter("");
                        setUsernameFilter("");
                        setEmailFromFilter("");
                        setMailerNameFilter("");
                      }}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading || filterLoading ? (
                  Array.from({ length: 10 }).map((_, rowIndex) => (
                    <tr key={rowIndex} className="skeleton-row">
                      {Array.from({ length: 8 }).map((_, colIndex) => (
                        <td key={colIndex}>
                          <div className="skeleton-box"></div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paginatedConfigs.length > 0 ? (
                  paginatedConfigs.map((config, index) => (
                    <tr key={config.id}>
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td>{config.smtp_host}</td>
                      <td>{config.smtp_username}</td>
                      <td>{config.smtp_secure}</td>
                      <td>{config.smtp_port}</td>
                      <td>{config.emailfrom}</td>
                      <td>{config.mailer_name}</td>
                      <td>
                        <button
                          className="btn btn-primary btn-xs"
                          onClick={() => checkMail(config.id, config.emailfrom)}
                          title="Send Test Mail"
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                        </button>
                        <PermissionWrapper module="settings_emailconfig" action="edit">
                          <Link
                            to={`/Settings/EmailConfig/edit/${config.id}`}
                            className="btn btn-warning btn-xs"
                            style={{ marginLeft: "5px" }}
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </Link>
                        </PermissionWrapper>
                        {/* <PermissionWrapper module="settings_emailconfig" action="delete">
                          <button
                            className="btn btn-danger btn-xs"
                            style={{ marginLeft: "5px" }}
                            onClick={() => handleDelete(config.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </PermissionWrapper> */}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center" }}>
                      No Records Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-3">No records found.</div>
        )}
      </div>
      {modalOpen && (
        <div
          className="modal-backdrop fade show"
          style={{
            backgroundColor: "rgba(0, 0, 0, 1)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1040,
          }}
        ></div>
      )}
      {modalOpen && (
        <div
          className="modal show"
          style={{ display: "block" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <div className="col-md-11">
                  <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                    Test Mail
                  </h5>
                </div>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => setModalOpen(false)}
                >
                  &times;
                </button>
                <div className="clearfix"></div>
              </div>
              <div className="modal-body">
                <form onSubmit={handleMailSubmit}>
                  <div className="form-group">
                    <label>From</label>
                    <input
                      type="text"
                      className="form-control"
                      value={mailData.mailfrom}
                      disabled
                    />
                  </div>
                  <div className="form-group">
                    <label>To</label>
                    <input
                      type="email"
                      className="form-control"
                      value={mailData.mail_to}
                      onChange={(e) =>
                        setMailData({ ...mailData, mail_to: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Content</label>
                    <textarea
                      className="form-control"
                      value={mailData.mailcontent}
                      onChange={(e) =>
                        setMailData({
                          ...mailData,
                          mailcontent: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className="form-group text-right">
                    <button type="submit" className="btn btn-primary mx-2">
                      Submit
                    </button>
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={() => setModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailConfigManagement;
